import React, { useEffect, useRef, useState } from 'react';
import { CalendarDays, ChevronDown } from 'lucide-react';
import { hasRange, normalizeRange, presetRange, rangeLabel, type DayRange, type RangePreset } from './activityViews';
import { DateField } from '../../components/ui/DateTimeField';
import type { Tone } from './activity';

/** SAM-1186 — the small controls of Activity (canvas v214): segmented view, time pills, Date range, Status, chips. */

export const TONE_CLASS: Record<Tone, string> = {
  ok: 'bg-[var(--ok-bg)] text-[var(--ok-fg)]',
  wait: 'bg-[var(--warn-bg-2)] text-[var(--warn-fg)]',
  off: 'bg-[var(--line-subtle)] text-[var(--ink-500)]',
  bad: 'bg-[var(--bad-bg)] text-[var(--bad-fg)]',
  info: 'bg-[var(--info-bg,#ebf1f5)] text-[var(--tenant-primary,#005488)]',
};

export const StatusChip: React.FC<{ tone: Tone; label: string; note?: string }> = ({ tone, label, note }) => (
  <span className="inline-flex flex-col items-start gap-0.5">
    <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap ${TONE_CLASS[tone]}`}>{label}</span>
    {note && <span className="text-[11px] text-[var(--ink-500)] pl-1">{note}</span>}
  </span>
);

export const Avatar: React.FC<{ initials: string }> = ({ initials }) => (
  <span aria-hidden className="shrink-0 w-8 h-8 rounded-full bg-[var(--info-bg,#ebf1f5)] text-[var(--tenant-primary,#005488)] text-xs font-bold flex items-center justify-center">
    {initials}
  </span>
);

export function Segmented<T extends string>({ label, value, options, onChange }: {
  label: string; value: T; options: Array<{ k: T; label: string }>; onChange: (k: T) => void;
}) {
  return (
    <div role="group" aria-label={label} className="inline-flex gap-1 p-[3px] rounded-[10px] bg-[var(--line-subtle)]">
      {options.map((o) => (
        <button
          key={o.k}
          type="button"
          aria-pressed={value === o.k}
          onClick={() => onChange(o.k)}
          className={`h-9 px-4 rounded-lg text-sm font-semibold border-0 cursor-pointer ${value === o.k ? 'bg-white text-[var(--ink-900)] shadow-sm' : 'bg-transparent text-[var(--ink-500)]'}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

const pill = (on: boolean) =>
  `h-9 px-4 rounded-full text-sm font-semibold border border-solid cursor-pointer inline-flex items-center gap-1.5 ${on
    ? 'bg-[var(--tenant-primary,#005488)] border-[var(--tenant-primary,#005488)] text-white'
    : 'bg-white border-[var(--line)] text-[var(--ink-700)]'}`;

export function Pills<T extends string>({ label, value, options, onChange }: {
  label: string; value: T | null; options: Array<{ k: T; label: string }>; onChange: (k: T) => void;
}) {
  return (
    <div role="group" aria-label={label} className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button key={o.k} type="button" aria-pressed={value === o.k} className={pill(value === o.k)} onClick={() => onChange(o.k)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

const PRESETS: Array<{ k: RangePreset; label: string }> = [
  { k: '7d', label: 'Last 7 days' },
  { k: '30d', label: 'Last 30 days' },
  { k: 'month', label: 'This month' },
  { k: 'last-month', label: 'Last month' },
];

export const DateRangeMenu: React.FC<{ value: DayRange; onChange: (r: DayRange) => void }> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DayRange>(value);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => { if (open) setDraft(value); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return undefined;
    const close = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button type="button" aria-haspopup="dialog" aria-expanded={open} className={pill(hasRange(value))} onClick={() => setOpen((o) => !o)}>
        <CalendarDays size={15} aria-hidden />
        {rangeLabel(value)}
        <ChevronDown size={14} aria-hidden />
      </button>
      {open && (
        <div role="dialog" aria-label="Date range" className="absolute right-0 top-11 z-20 w-72 rounded-xl border border-solid border-[var(--line)] bg-white shadow-lg p-3 flex flex-col gap-3">
          <div className="flex flex-wrap gap-1.5">
            {PRESETS.map((p) => (
              <button key={p.k} type="button" className="h-8 px-3 rounded-full border border-solid border-[var(--line)] bg-white text-[13px] font-semibold cursor-pointer" onClick={() => setDraft(presetRange(p.k))}>
                {p.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <DateField label="From" ariaLabel="From" value={draft.from ?? ''} placeholder="Choose a day" onChange={(from) => setDraft((d) => ({ ...d, from: from || undefined }))} />
            <DateField label="To" ariaLabel="To" value={draft.to ?? ''} placeholder="Choose a day" onChange={(to) => setDraft((d) => ({ ...d, to: to || undefined }))} />
          </div>
          <div className="flex justify-between">
            <button type="button" className="h-9 px-3 border-0 bg-transparent text-sm font-semibold text-[var(--ink-500)] cursor-pointer" onClick={() => { onChange({}); setOpen(false); }}>
              Clear
            </button>
            <button type="button" className="h-9 px-4 rounded-lg border-0 bg-[var(--tenant-primary,#005488)] text-white text-sm font-semibold cursor-pointer" onClick={() => { onChange(normalizeRange(draft)); setOpen(false); }}>
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export function StatusMenu<T extends string>({ value, options, onChange }: {
  value: T; options: Array<{ k: T; label: string }>; onChange: (k: T) => void;
}) {
  return (
    <label className={`${pill(value !== 'all')} relative`}>
      <span className={value !== 'all' ? 'opacity-80 font-medium' : 'text-[var(--ink-500)] font-medium'}>Status</span>
      {options.find((o) => o.k === value)?.label}
      <ChevronDown size={14} aria-hidden />
      <select
        aria-label="Status"
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
      >
        {options.map((o) => <option key={o.k} value={o.k}>{o.label}</option>)}
      </select>
    </label>
  );
}
